<template>
  <div v-if="form">
    <b-form @submit="submitForm">
      <!-- RMA/HC Type -->
      <section class='mb-3'>
        <div v-if="isHc">
          <h2>Problem (HC)</h2>
          <b-form-radio-group required v-model="form.type" :options="problemTypes" buttons class='hc my-1' @change=onChangeType />
          <b-form-input v-if="form.type === 'MiscIssue'" required placeholder="More Info (*)" v-model="form.details" class='my-1' />
          <b-form-input required placeholder="How Many? (*)" type="number" v-model="form.qty" class='my-1' min=1 />
        </div>
        <div v-else>
          <h2>RMA Reason</h2>
          <b-form-radio-group required v-model="form.type" :options="rmaTypes" buttons class='rma my-1' @change=onChangeType />
          <b-form-input v-if="form.type === 'misc'" required placeholder="More Info (*)" v-model="form.details" class='my-1' />
          <b-form-input required placeholder="How Many? (*)" type="number" v-model="form.qty" class='my-1' min=1 />
          <b-form-row v-if="!isHc">
            <b-col v-if="currentItem">
              Item Price: {{ currentUnitPrice * form.qty | formatDollars }}<br/>
              Refund Amount: {{ form.refundAmount | formatDollars }}
            </b-col>
            <b-col cols=*>
              Restock Percent:
            </b-col>
            <b-col>
              <b-input-group append="%">
                <b-form-input
                  required
                  type="number" min=0 max=100
                  v-model="form.restockPercent"
                  @input="updateRefundPrices"
                />
              </b-input-group>
            </b-col>
          </b-form-row>
        </div>
        <hr/>
      </section>

      <section v-if="isHc && form.type == 'WrongPart'" class='my-3'>
        <b-form-row>
          <b-col>
            <h2>What Did We Send? (Wrong Product)</h2>
          </b-col>
        </b-form-row>
        <b-form-row v-for="(row,ix) in form.wrongPart" :key="`wrongpart-${ix}`" class='my-2 py-1' style='background-color:lightgrey'>
          <b-alert :show="row.alert" variant="warning">{{row.alert}}</b-alert>
          <b-col>
            <b-form-row class='mt-1'>
              <b-col>
                <product-search-input :default="row.sku || ''" @select="(r) => onSetSku(row, r)" v-if="!reload" />
              </b-col>
              <b-col cols=3>
                <b-form-input required placeholder="How Many? (*)" type="number" min=1 v-model="form.wrongPart[ix].qty" />
              </b-col>
              <b-col cols=*>
                <label>Getting this back?</label>
                <b-form-checkbox switch inline v-model="row.touchInventory" />
              </b-col>
            </b-form-row>
          </b-col>
          <b-col cols=1>
            <b-btn @click="removeItem(form.wrongPart, ix)">
              <i class="fas fa-trash"></i>
            </b-btn>
          </b-col>
        </b-form-row>
        <b-form-row class='my-1'>
          <b-col />
          <b-col cols=1>
            <b-btn @click="addWrongPartItem">
              <i class="fas fa-plus"></i>
            </b-btn>
          </b-col>
        </b-form-row>
        <hr/>
      </section>

      <!-- What Are We Sending? -->
      <section v-if="isHc" class='my-3'>
        <b-form-row>
          <b-col>
            <h2>What Are We Sending?</h2>
          </b-col>
        </b-form-row>
        <b-form-row v-for="(row,ix) in form.toSend" :key="`send-${ix}`" class='my-2 py-1' style='background-color:lightgrey'>
          <b-alert :show="row.alert" variant="warning">{{row.alert}}</b-alert>
          <b-col>
            <b-form-row class='mt-1'>
              <b-col>
                <product-search-input :default="row.sku || currentSku" @select="(r) => onSetSku(row, r)" v-if="!reload" />
              </b-col>
              <b-col cols=3>
                <b-form-input required placeholder="How Many? (*)" type="number" min=1 v-model="form.toSend[ix].qty" @change="refreshItemNote()" />
              </b-col>
              <b-col cols=*>
                <label class="mb-0">Send From Stock</label>
                <b-form-checkbox style="margin-top:2px" switch inline v-model="form.toSend[ix].touchInventory" @change="refreshItemNote()" />
              </b-col>
            </b-form-row>
          </b-col>
          <b-col cols=1>
            <b-btn @click="removeItem(form.toSend, ix)">
              <i class="fas fa-trash"></i>
            </b-btn>
          </b-col>
        </b-form-row>
        <b-form-row class='my-1'>
          <b-col />
          <b-col cols=1>
            <b-btn @click="addSendItem">
              <i class="fas fa-plus"></i>
            </b-btn>
          </b-col>
        </b-form-row>
        <hr/>
      </section>

      <!-- What Are We Getting Back? -->
      <section class='my-3'>
        <b-form-row>
          <b-col>
            <h2>What Are We Getting Back?</h2>
          </b-col>
        </b-form-row>
        <b-form-row v-if="form.type === 'WrongPart'">
          <b-col>
            Please don't include any items we incorrectly sent the customer, those go in the <strong>What Did We Send? (Wrong Product)</strong> section. This should only be misc. other items we're getting back, and won't be used in most orders
          </b-col>
        </b-form-row>
        <b-form-row v-for="(row,ix) in form.toReceive" :key="`receive-${ix}`" style='background-color:lightgrey' class='py-1 my-2'>
          <b-col>
            <b-form-row>
              <b-col>
                <b-alert :show=row.alert variant=warning class='my-1'>{{row.alert}}</b-alert>
              </b-col>
            </b-form-row>
            <b-form-row>
              <b-col>
                <b-form-radio-group required v-model="row.type" :options="receiveTypes" buttons :class="`${isHc ? 'hc' : 'rma'} mb-1`" />
              </b-col>
              <b-col cols=3>
                <b-form-input required placeholder="How Many? (*)" type=number min=1 v-model="row.qty" class="mt-1" @change="updateRefundPrices" />
              </b-col>
              <!-- <b-col cols=*>
                <label>Receive to Inventory?</label>
                <b-form-checkbox switch inline v-model="row.touchInventory" class="mt-1" />
              </b-col> -->
            </b-form-row>
            <b-form-row>
              <b-col>
                <b-row v-if="row.type === 'sku'">
                  <b-col>
                    <product-search-input :default="row.sku || currentSku" @select="(r) => onSetSku(row, r)" class='mt-1' v-if="!reload" />
                  </b-col>
                </b-row>
                <b-row v-else-if="row.type === 'image'">
                  <b-col cols=4 class='pt-1'>
                    <label>Images coming later?</label>
                    <b-form-checkbox inline switch v-model=row.imagesLater />
                  </b-col>
                  <b-col>
                    <b-row>
                      <b-col>
                        <b-form-file multiple :required="!row.imagesLater && row.images.length === 0" v-if="!row.imagesLater" accept="image/*" @input="(input) => onChangeReceiveImages(row, input)" />
                      </b-col>
                    </b-row>
                    <b-row v-if="row.images.length !== 0">
                      <b-col>
                        <b-alert show class='py-0 mt-2' variant=success>
                          {{ row.images.length }} image{{ row.images.length === 1 ? '' : 's' }} loaded!
                        </b-alert>
                      </b-col>
                    </b-row>
                  </b-col>
                </b-row>
                <span v-else-if="row.type === 'notSure' && !row.typeBypassed">
                  So we're getting items back, but we don't know what, and the customer isn't willing to provide this information?
                  <b-row align-v="center">
                    <b-col>
                      <center>
                        <b-btn @click="row.typeBypassed = true" size='sm' variant='danger' class='m-0 p-0' v-b-popover.hover="'Before clicking, can we get that information? It helps identify issues!'">
                          Yes
                        </b-btn>
                      </center>
                    </b-col>
                    <b-col>
                      <center>
                        <b-btn @click="row.type = null" size='lg' variant='success'>
                          No
                        </b-btn>
                      </center>
                    </b-col>
                    <b-col cols=3>
                    </b-col>
                  </b-row>
                </span>
              </b-col>
            </b-form-row>
          </b-col>
          <b-col cols=1 class="align-content-center">
            <b-btn @click="removeItem(form.toReceive, ix)">
              <i class="fas fa-trash"></i>
            </b-btn>
          </b-col>
        </b-form-row>
        <b-form-row v-if="form.toReceive.length === 0">
          <b-col>
            Not receiving an item from the customer
          </b-col>
        </b-form-row>
        <b-form-row class='my-1'>
          <b-col />
          <b-col cols=1>
            <b-btn @click="addReceiveItem">
              <i class="fas fa-plus"></i>
            </b-btn>
          </b-col>
        </b-form-row>
        <hr/>
      </section>

      <section class='my-3'>
        <h2>Item Note</h2>
        <b-form-row class='my-2'>
          <b-col>
            <b-input v-model="form.userNotes" placeholder="Item Note" @input=handleItemNoteChange />
          </b-col>
        </b-form-row>
        <b-form-row class='my-2'>
          <b-col>
            <b-input v-model="form.notes" disabled />
          </b-col>
        </b-form-row>
      </section>

      <b-form-row style='margin-top:30px;'>
        <b-col>
          <b-alert show v-if="form.alert" variant="warning" class='mb-1'>
            {{form.alert}}
          </b-alert>
          <b-btn block variant='primary' type='submit'>{{this.isNewItem ? "Add" : "Edit"}} Item</b-btn>
        </b-col>
      </b-form-row>
    </b-form>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

import utility from '../../helpers/utility'
import rmahcService from '../../services/rmahc'
import mergeService from '../../services/orderMerge'
import ProductSearchInput from '../utility/ProductSearchInput.vue'

export default {
  data() {
    return {
      reload: false,
      form: null,
      defaultSendFromStock: true,
    }
  },
  props: {
    isHc: {
      required: true,
      type: Boolean
    },
    currentItem: {
      required: false,
      type: Object,
      default: () => undefined
    },
    currentLine: {
      required: false,
      type: Object,
      default: () => undefined
    },
    defaultExtra: {
      required: false,
      type: Boolean,
      default: false,
    }
  },
  components: {
    ProductSearchInput,
  },
  mounted() {
    this.getLoggedInUser()
    this.resetForm()
    if (this.currentLine) {
      this.form = this.currentLine
    }
    if (this.defaultExtra) {
      this.form.type = this.isHc ? 'Extra' : 'misc'
    }
    if (this.isNewItem && !this.isHc) {
      this.addReceiveItem()
    }
  },
  computed: {
    ...mapGetters({
      loggedInUser: 'loggedInUser',
    }),
    isNewItem() { return this.form.editIndex === null },
    problemTypes() { return rmahcService.problemTypes; },
    rmaTypes() { return rmahcService.rmaTypes; },
    receiveTypes() { return rmahcService.receiveTypes; },
    currentUnitPrice() {
      if (this.currentItem) {
        return this.currentItem.percentileUnitPrice ? this.currentItem.percentileUnitPrice : mergeService.mergeField(this.currentItem.unitPrice)
      } else {
        return 0
      }
    },
    currentItemQty() {
      if (this.currentItem && this.currentItem.qty) {
        return mergeService.mergeField(this.currentItem.qty);
      } else {
        return 1
      }
    },
    currentSku() {
      return this.currentItem ? mergeService.mergeField(this.currentItem.sku) : ''
    },
  },
  methods: {
    ...mapActions({
      getLoggedInUser: 'getLoggedInUser',
    }),
    blankItem() { return rmahcService.blankItem(this.currentSku, this.form.qty ? this.form.qty : this.currentItemQty) },
    resetForm() {
      this.form = rmahcService.blankLine(this.currentItem, this.currentItemQty);
    },

    onChangeType(input) {
      this.defaultSendFromStock = input !== 'Missing' && input !== 'WrongPart'
    },
    onChangeReceiveImages(row, input) {
      row.images.splice(0)
      input.forEach(image => {
        let reader = new FileReader()
        reader.onloadend = () => {
          row.images.push(reader.result);
        }
        reader.readAsDataURL(image)
      })
    },

    addProblem(event) {
      if (event) event.preventDefault();
    },
    updateRefundPrices() {
      rmahcService.updateRefundPrices(this.isHc, this.form.toReceive, this.form.restockPercent)
      this.form.refundAmount = this.form.toReceive.reduce((memo, i) => memo + (i.unitPrice - i.unitRestockPrice) * i.qty, 0)
    },
    onSetSku(row, result) {
      let shouldRefresh = row.sku !== result.num
      row.type = 'sku'
      row.sku = result.num;
      row.productId = result.id;
      row.taxable = result.taxableFlag;
      row.isKit = result.kitFlag;

      if (!this.isHc) {
        const kitItems = utility.dig(this.currentItem, 'kitItems', 'fishbowl')
        if (kitItems.some(x => mergeService.mergeField(x.sku) === result.num)) {
          let match = kitItems.filter(x => mergeService.mergeField(x.sku) === result.num)[0]
          console.info(`using percentile unit price of ${match.percentileUnitPrice} - real price was ${result.price}`)
          row.unitPrice = match.percentileUnitPrice
        } else if (row.sku == this.currentSku) {
          row.unitPrice = this.currentUnitPrice
        } else {
          row.unitPrice = result.price
        }
      }
      this.updateRefundPrices()
      this.refreshItemNote()

      if (shouldRefresh) this.refreshComponents()
    },
    refreshItemNote() {
      this.handleItemNoteChange(this.form.userNotes)
    },
    removeItem(arr, ix) {
      arr.splice(ix, 1);
      this.refreshComponents()
    },

    addWrongPartItem(event) {
      if (event) event.preventDefault();

      this.form.wrongPart.forEach(line => {
        rmahcService.validateItem(line)
      })
      if (this.form.wrongPart.some(line => line.alert)) return

      let newItem = this.blankItem()
      newItem.sku = null
      this.form.wrongPart.push(newItem);
    },
    addSendItem(event) {
      if (event) event.preventDefault();

      this.form.toSend.forEach(line => {
        rmahcService.validateItem(line)
      })
      if (this.form.toSend.some(line => line.alert)) return

      let newItem = this.blankItem();
      newItem.touchInventory = this.defaultSendFromStock
      this.form.toSend.push(newItem);
      this.handleItemNoteChange(this.form.userNotes)
    },
    addReceiveItem(event) {
      if (event) event.preventDefault();

      this.form.toReceive.forEach(line => {
        rmahcService.validateItem(line)
        if (!line.type) {
          line.alert = 'Please select one of the Return Types!'
        } else if (line.type !== 'sku') {
          line.unitPrice = 0;
        }
      })
      if (this.form.toReceive.some(line => line.alert)) return

      this.form.toReceive.push(this.blankItem());
      this.updateRefundPrices()
    },

    handleItemNoteChange(input) {
      this.form.notes = rmahcService.getItemNote(input, this.form.toSend, this.loggedInUser.names)
    },

    submitForm(event) {
      if (event) event.preventDefault();

      if (this.form.type !== 'WrongPart') {
        this.form.wrongPart.splice(0)
      }

      this.form.alert = null
      if (this.isHc) {
        if (this.form.type === null) {
          this.form.alert = 'Need to submit a Problem Type'
        } else if (this.form.type !== 'Extra' && this.form.toSend.length === 0) {
          this.form.alert = 'Need to submit at least 1 Item to Send'
        }
      } else {
        if (this.form.type === null) {
          this.form.alert = 'Need to submit a RMA Reason'
        }
      }
      if (this.form.alert) return

      this.$emit('submit', this.form)
    },

    async refreshComponents() {
      this.reload = true;
      await this.$nextTick();
      this.reload = false
    }
  },
}
</script>

<style>
.btn-group-toggle.hc > .btn-secondary:not(:disabled):not(.disabled):not(.active):hover {
  background-color: #80f !important;
  border-color: #80f !important;
}
.btn-group-toggle.hc > .btn-secondary:not(:disabled):not(.disabled).active {
  background-color: #60c !important;
  border-color: #60c !important;
}
.btn-group-toggle.rma > .btn-secondary:not(:disabled):not(.disabled):not(.active):hover {
  background-color: #ffc107 !important;
  border-color: #ffc107 !important;
  color: black !important;
}
.btn-group-toggle.rma > .btn-secondary:not(:disabled):not(.disabled).active {
  background-color: #ffc107 !important;
  border-color: #ffc107 !important;
  color: black !important;
}
</style>



// WEBPACK FOOTER //
// src/components/rma-hc/ItemForm.vue